import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import TotpCodeField, { isCompleteCode } from '../TotpCodeField';
import {
  unenrollFactor,
  InvalidCredentialsError,
  InvalidCodeError,
  SessionExpiredError,
} from '../../services/mfaService';

/**
 * Turning MFA off.
 *
 * Both a password and a current code are required — an MFA-enrolled user cannot
 * obtain an ID token from a password alone, so the server needs both proofs.
 *
 * This keeps the server's message where the enrollment dialog replaces it, and
 * the difference is not inconsistency. There, "incorrect or has expired" sits
 * beside a visible setup session and invites the user to think that session
 * died and they must start over. Here there is no session on screen at all, so
 * the same words can only be read as being about the code — which is accurate,
 * since a TOTP code really does expire in its own 30-second step. Nothing to
 * misdirect, so nothing to override.
 *
 * The copy says "turn off multi-factor authentication" rather than "remove this
 * factor" because that is what the endpoint does: it clears every factor the
 * provider holds, and the enrollment guards ensure at most one can exist
 * (mfa_service.go:60-75). A per-factor Remove would be a lie about the button.
 */
function DisableMfaDialog({ open, onClose, factorId, onDisabled }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [formError, setFormError] = useState(null);

  const resetRef = useRef();
  const reset = useCallback(() => {
    setPassword('');
    setCode('');
    setBusy(false);
    setPasswordError(null);
    setCodeError(null);
    setFormError(null);
  }, []);
  resetRef.current = reset;

  useEffect(() => {
    if (!open) resetRef.current();
  }, [open]);

  // Editing the code retracts the verdict on the last one, the same way
  // MfaCodeStep does it. Nothing here would clear the error otherwise until the
  // next submit, so the field would keep telling the user their code is wrong
  // while they are in the middle of typing the replacement.
  const handleCodeChange = (next) => {
    setCode(next);
    setCodeError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setPasswordError(null);
    setCodeError(null);
    setFormError(null);

    try {
      await unenrollFactor(factorId, { password, code });
      onDisabled();
    } catch (err) {
      if (err instanceof InvalidCredentialsError) {
        setPasswordError(err.message);
      } else if (err instanceof InvalidCodeError) {
        setCodeError(err.message);
      } else if (err instanceof SessionExpiredError) {
        // Redirect underway.
      } else {
        setFormError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Turn off multi-factor authentication</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Your account will be protected by your password alone. Confirm your password and
            a current code from your authenticator app.
          </DialogContentText>

          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <TextField
            id="disable-password"
            name="password"
            label="Current password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={Boolean(passwordError)}
            helperText={passwordError || ' '}
            disabled={busy}
            fullWidth
            autoFocus
            // Focus is on Turn off by the time a rejected password comes back,
            // and helper text is only read out when the field it describes is
            // focused — so without a live region the user presses the button
            // and hears nothing. Only while there is an error: the slot
            // otherwise holds a blank spacer, and an empty assertive region is
            // noise.
            slotProps={{ formHelperText: passwordError ? { role: 'alert' } : undefined }}
          />

          <TotpCodeField
            id="disable-code"
            label="Six-digit code"
            value={code}
            onChange={handleCodeChange}
            errorText={codeError}
            helperText=" "
            disabled={busy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={!password || !isCompleteCode(code) || busy}
          >
            Turn off
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default DisableMfaDialog;
